import { StorageLocationPutawayMode } from "@redotech/crdb-enums/fulfillment/storage-location-putaway-mode";
import { accountIdFormat } from "@redotech/redo-account-id";
import {
  customReservedInventoryBucketTypeIdFormat,
  inventoryItemIdFormat,
  locationIdFormat,
} from "@redotech/redo-inventory-id";
import { productIdFormat } from "@redotech/redo-product-id";
import { storageLocationIdFormat } from "@redotech/redo-warehouse-id";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum StorageLocationType {
  BIN = "bin",
  TOTE = "tote",
  STORAGE = "storage",
  RECEIVING = "receiving",
  EXTERNAL = "external",
  LOST = "lost",
  TRANSIT = "transit",
  UNMAPPED = "unmapped",
  DEFAULT = "default",
  WASTE = "waste",
  FLUSH = "flush",
  STATION = "station",
  SORTING_TOTE = "sorting_tote",
  STAGING = "staging",
  SUPPLIES = "supplies",
}

// these show up in the bin table and are not transient, (totes or carts)
export const PersistentLocationTypes: StorageLocationType[] = [
  StorageLocationType.BIN,
  StorageLocationType.STORAGE,
  StorageLocationType.RECEIVING,
  StorageLocationType.STATION,
  StorageLocationType.SORTING_TOTE,
];

export const CreatableStorageLocationTypeSchema = createEnum(
  [
    StorageLocationType.BIN,
    StorageLocationType.STORAGE,
    StorageLocationType.RECEIVING,
  ],
  { enumName: "CreatableStorageLocationType" },
).meta({ privacy: privacy.nonPii });
export type CreatableStorageLocationType = z.infer<
  typeof CreatableStorageLocationTypeSchema
>;

// Create bins cannot insert stations or sorting totes.
export const CreatableLocationTypes: CreatableStorageLocationType[] = [
  StorageLocationType.BIN,
  StorageLocationType.STORAGE,
  StorageLocationType.RECEIVING,
];

// Real storage only. Totes and receiving hold stock in transit, so counting
// them measures a moving target rather than shelf truth.
export const CycleCountableLocationTypes: StorageLocationType[] = [
  StorageLocationType.BIN,
  StorageLocationType.STORAGE,
  StorageLocationType.DEFAULT,
];

// Bins a count can target as its unit of work: countable storage minus the
// DEFAULT catch-all, which a count resolves rather than lists.
export const CycleCountTargetBinTypes: StorageLocationType[] = [
  StorageLocationType.BIN,
  StorageLocationType.STORAGE,
];

// Where WMS does not own on-hand, IMS keeps it all in the DEFAULT bin, so that
// is the only bin a count reads.
export const CycleCountDefaultBinTypes: StorageLocationType[] = [
  StorageLocationType.DEFAULT,
];

export const PersistentStorageLocationTypeSchema = createEnum(
  [
    StorageLocationType.BIN,
    StorageLocationType.STORAGE,
    StorageLocationType.RECEIVING,
    StorageLocationType.STATION,
    StorageLocationType.SORTING_TOTE,
  ],
  { enumName: "PersistentStorageLocationType" },
).meta({ privacy: privacy.nonPii });
export type PersistentStorageLocationType = z.infer<
  typeof PersistentStorageLocationTypeSchema
>;

export const BinPriority = ["primary", "secondary"] as const;
export type BinPriority = (typeof BinPriority)[number];
export const BinPrioritySchema = createEnum(BinPriority, {
  enumName: "BinPriority",
}).meta({ privacy: privacy.nonPii });

export enum StorageLocationSource {
  MANUAL = "manual",
  WAREHOUSE_MAPPER = "warehouse_mapper",
  SYSTEM = "system",
}

export const StorageLocationTypeSchema = createEnum(StorageLocationType).meta({
  privacy: privacy.nonPii,
});
export const StorageLocationSourceSchema = createEnum(
  StorageLocationSource,
).meta({ privacy: privacy.nonPii });

export const StorageLocationPutawayModeSchema = createEnum(
  StorageLocationPutawayMode,
).meta({ privacy: privacy.nonPii });

export const ReservedBucketCategorySchema = z
  .object({
    type: z.literal("custom").meta({ privacy: privacy.nonPii }),
    customTypeId: customReservedInventoryBucketTypeIdFormat.schema.meta({
      privacy: privacy.nonPii,
    }),
  })
  .nullable()
  .meta({ privacy: privacy.structured });
export type ReservedBucketCategory = z.infer<
  typeof ReservedBucketCategorySchema
>;

export const StorageLocationSchema = z
  .object({
    id: storageLocationIdFormat.schema.meta({ privacy: privacy.id.internal }),
    accountId: accountIdFormat.schema.meta({ privacy: privacy.id.internal }),
    locationId: locationIdFormat.schema.meta({ privacy: privacy.id.internal }),
    name: z.string().meta({ privacy: privacy.resourceLabel }),
    type: StorageLocationTypeSchema.meta({ privacy: privacy.nonPii }),
    source: StorageLocationSourceSchema.meta({ privacy: privacy.nonPii }),
    isSellable: z.boolean().meta({ privacy: privacy.flag }),
    reservedBucketCategory: ReservedBucketCategorySchema.meta({
      privacy: privacy.structured,
    }),
    deletedAt: z.date().nullable().meta({ privacy: privacy.nonPii }),
    createdAt: z.date().meta({ privacy: privacy.nonPii }),
    updatedAt: z.date().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

export type StorageLocation = z.infer<typeof StorageLocationSchema>;
export interface IStorageLocation extends z.ZodType<StorageLocation> {}

export const StorageLocationInventoryItemSchema = z
  .object({
    productInfo: z
      .object({
        id: productIdFormat.schema.meta({ privacy: privacy.nonPii }),
        title: z.string().meta({ privacy: privacy.productCatalogText }),
        imageUrl: z.string().nullish().meta({ privacy: privacy.publicUrl }),
        sku: z.string().nullish().meta({ privacy: privacy.productSku }),
        barcode: z.string().nullish().meta({ privacy: privacy.nonPii }),
        barcodes: z
          .array(z.string().meta({ privacy: privacy.nonPii }))
          .nullish()
          .meta({ privacy: privacy.structured }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    inventoryItemId: inventoryItemIdFormat.schema
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    inventoryItemKind: z.string().nullish().meta({ privacy: privacy.nonPii }),
    quantity: z.number().meta({ privacy: privacy.quantity }),
    /** Reserved by open source-side transfers; only populated where asked for. */
    committed: z.number().nullish().meta({ privacy: privacy.nonPii }),
    lastCountedAt: z.coerce.date().nullish().meta({ privacy: privacy.nonPii }),
    lotNumber: z.string().nullish().meta({ privacy: privacy.id.serialOrLot }),
    expirationDate: z.coerce.date().nullish().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

export const StorageLocationTableRowSchema = StorageLocationSchema.extend({
  inventoryItems: z
    .array(
      StorageLocationInventoryItemSchema.meta({ privacy: privacy.structured }),
    )
    .meta({ privacy: privacy.structured }),
  locationName: z.string().meta({ privacy: privacy.resourceLabel }),
  putawayMode: StorageLocationPutawayModeSchema.nullish().meta({
    privacy: privacy.nonPii,
  }),
}).meta({ privacy: privacy.structured });

export type StorageLocationTableRow = z.infer<
  typeof StorageLocationTableRowSchema
>;
